import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TFetchParams } from '@/types/fetch.type';
import { IPagination } from '@/types/general.type';
import { TLabelAuto } from '../types/label-auto.type';

/**
 * Get List Label Auto Response Type
 */
export type TGetListLabelAutoResponse = ApiResponse<IPagination<TLabelAuto>>;
export type TGetListLabelAutoParams = TFetchParams;

export const getListLabelAuto = async (params: TGetListLabelAutoParams): Promise<TGetListLabelAutoResponse> => {
  const url = '/auto-label';

  return axiosInstance.get(url, { params });
};

/**
 * Get List Label Auto React-Query Hook
 */
export const GET_LIST_LABEL_AUTO_QUERY_KEY = 'list-label-auto';

export const getListLabelAutoQueryOptions = <TData = IPagination<TLabelAuto>>(
  params: TGetListLabelAutoParams,
  select?: (data: TGetListLabelAutoResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_LABEL_AUTO_QUERY_KEY,
      {
        page: params?.page,
        limit: params?.limit,
        search: params?.search,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getListLabelAuto(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListLabelAutoQuery<TData>(options: {
  params: TGetListLabelAutoParams;
  queryConfig?: QueryConfig<typeof getListLabelAutoQueryOptions<TData>>;
  select: (data: TGetListLabelAutoResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListLabelAutoQuery(options: {
  params: TGetListLabelAutoParams;
  queryConfig?: QueryConfig<typeof getListLabelAutoQueryOptions<TGetListLabelAutoResponse>>;
}): ReturnType<typeof useQuery<TGetListLabelAutoResponse>>;

// Implementation
export function useGetListLabelAutoQuery<TData = TGetListLabelAutoResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListLabelAutoParams;
  queryConfig?: QueryConfig<typeof getListLabelAutoQueryOptions<TData | TGetListLabelAutoResponse>>;
  select?: (data: TGetListLabelAutoResponse) => TData;
}) {
  return useQuery({
    ...getListLabelAutoQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListLabelAutoResponse ? TGetListLabelAutoResponse : TData>>;
}
